import React, { useState, useMemo, useCallback, useEffect } from 'react';
import DraggableDialog from 'spw-components/dialogs/draggable-dialog';
import { isNil } from 'ramda';
import styled from 'styled-components';
import { Row } from 'spw-shared/globalComponents';
import { Box, Button, IconButton, Typography } from '@mui/material';

import { Clear } from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import {
  currentProject,
  projectColors as selectProjectColors,
  selectCurrentProjectColors,
  selectIndividualWorkspacesCategory,
  selectProjectSpaceTypesByCategory,
  selectProjectSpaceTypesById,
} from 'spw-redux/modules/projects/selectors';
import RestoreIcon from 'spw-static/images/restore.svg';
import RestoreGrayIcon from 'spw-static/images/restore-gray.svg';
import { colors } from 'spw-theme';
import {
  IProjectSpaceTypeCategory,
  IProjectSpaceTypeSubcategory,
} from 'spw-redux/modules/projects/types';
import { buildColorGradient } from 'spw-projects/project-profile/space-categories-and-colors/utils';
import {
  APP_HEADER_HEIGHT_IN_PX,
  CUSTOM_SUBCATEGORY,
  INDIVIDUAL_WORKSPACES_CATEGORY,
  PRIVATE_OFFICES_SUBCATEGORY,
  WORKSTATIONS_SUBCATEGORY,
  CUSTOM_WORKSPACE_COLORS,
} from 'spw-constants';
import SubcategoryColorGradient from 'spw-projects/space_type_categories/subcategory-color-gradient';
import {
  setCurrentProjectColors,
  updateProjectColors,
} from 'spw-redux/modules/projects/actions';
import { IProjectSpaceType } from 'spw-redux/modules/space_types/types';
import SpaceTypeColorGradient from 'spw-projects/space_types/space-type-color-gradient';
import { isEqual } from 'lodash';
import {
  selectClientFacingNamesAreVisible,
  selectCurrentSpaceTypeColors,
} from 'spw-redux/modules/space_types/selectors';
import { setCurrentSpaceTypeColors } from 'spw-redux/modules/space_types/actions';
import {
  buildCurrentProjectSubcategoryColors,
  buildDefaultSpaceTypeColors,
} from 'spw-projects/space_types/utils';
import { selectBreakdown } from 'spw-redux/modules/program-summary/esa/selectors';
import { ISpaceBreakDown } from 'spw-redux/modules/program-summary/types';
import { CategoryColorBubble, SmallCategoryColorBubble } from '../../styles';

const DIALOG_HEIGHT = 600;
const DIALOG_WIDTH = 356;

const PopoverHeader = styled.div`
  padding: 24px 32px 16px 32px;
  cursor: grab;
  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
`;

const RestoreButton = styled(Button)`
  &&.MuiButton-root {
    padding: 0;
    background: ${colors.white};
    text-transform: none;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px;
  }

  color: ${colors.mainBlue};
`;

const RestoreImage = styled.img`
  margin-right: 4px;
  width: 12px;
  height: 12px;
`;

const PopoverContent = styled.div`
  padding: 0 32px 0 32px;
  height: calc(100% - 80px);
  overflow-y: auto;
  overflow-x: hidden;

  > div:first-child {
    width: 292px;
  }

  p {
    margin: 16px 0;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
  }

  h5 {
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 20px;
  }

  .subtitle {
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px;
  }
`;

const GradientTitle = styled.div`
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
`;

const Section = styled.div`
  border-bottom: solid 1px ${colors.offWhite};

  &.borderless {
    border-bottom: unset;
  }
`;

type ProjectCategory = IProjectSpaceTypeCategory & { color: string };

type Props = {
  anchorEl?: HTMLElement | null;
  onClose: () => void;
};

const SpaceTypeColorsPopover: React.FC<Props> = ({ anchorEl, onClose }) => {
  const project = useSelector(currentProject);
  const individualWorkspacesCategory = useSelector(
    selectIndividualWorkspacesCategory
  );
  const projectColors = useSelector(selectProjectColors);
  const currentProjectColors = useSelector(selectCurrentProjectColors);
  const currentSpaceTypeColors = useSelector(selectCurrentSpaceTypeColors);
  const spaceTypesGroupedByCategory = useSelector(
    selectProjectSpaceTypesByCategory
  );
  const spaceTypesById: Record<number, IProjectSpaceType> = useSelector(
    selectProjectSpaceTypesById
  );
  const spacesBreakdown = useSelector(selectBreakdown);
  const [dialogClientRect, setDialogClientRect] = useState<DOMRect>();
  const clientFacingNamesAreVisible: boolean = useSelector(
    selectClientFacingNamesAreVisible
  );

  const processRefNode = useCallback((node: HTMLElement) => {
    setDialogClientRect(node.getBoundingClientRect());
  }, []);

  const [bounds, setBounds] = useState({
    left: 0,
    right: 0,
    bottom: 0,
    top: 0,
  });

  const subcategoryColors: Record<
    string,
    { color: string; gradientColorIndex: number }
  > = currentProjectColors.subcategories;

  const dispatch = useDispatch();

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [initialPosition, setInitialPosition] = useState({
    top: anchorEl.getBoundingClientRect().bottom - 10,
    left: anchorEl.getBoundingClientRect().left - 100,
  });

  const initialColors = useMemo(() => {
    return {
      subcategoryColors: buildCurrentProjectSubcategoryColors(projectColors),
    };
  }, [projectColors]);

  const selectedCategoryGradientColors = useMemo(() => {
    if (selectedCategory) {
      return buildColorGradient(
        projectColors[
          selectedCategory.defaultSpaceTypeCategory.category
        ] as string
      );
    }
    return [];
  }, [projectColors, selectedCategory]);

  const defaultSubcategoryColors = useMemo(() => {
    const workstationsColor = projectColors[CUSTOM_WORKSPACE_COLORS][1];
    const privateOfficesColor = projectColors[CUSTOM_WORKSPACE_COLORS][3];
    const customSubcategoryColor = projectColors[CUSTOM_WORKSPACE_COLORS][7];

    return {
      [WORKSTATIONS_SUBCATEGORY]: {
        color: workstationsColor,
        gradientColorIndex: selectedCategoryGradientColors.indexOf(
          workstationsColor
        ),
      },
      [PRIVATE_OFFICES_SUBCATEGORY]: {
        color: privateOfficesColor,
        gradientColorIndex: selectedCategoryGradientColors.indexOf(
          privateOfficesColor
        ),
      },
      [CUSTOM_SUBCATEGORY]: {
        color: customSubcategoryColor,
        gradientColorIndex: selectedCategoryGradientColors.indexOf(
          customSubcategoryColor
        ),
      },
    };
  }, [projectColors, selectedCategoryGradientColors]);

  const categories: ProjectCategory[] = useMemo(() => {
    return project.spaceTypeCategories.map(
      (spaceTypeCategory: IProjectSpaceTypeCategory) => ({
        ...spaceTypeCategory,
        color: projectColors[
          spaceTypeCategory.defaultSpaceTypeCategory.category
        ] as string,
      })
    );
  }, [project.spaceTypeCategories, projectColors]);

  useEffect(() => {
    if (!selectedCategory) {
      setSelectedCategory({
        ...individualWorkspacesCategory,
        color: projectColors[INDIVIDUAL_WORKSPACES_CATEGORY],
      });
    }
  }, [selectedCategory, individualWorkspacesCategory, projectColors]);

  const onSelectSubcategoryColor = useCallback(
    (subcategory: string, colorIndex: number) => {
      const color = selectedCategoryGradientColors[colorIndex];

      dispatch(
        setCurrentProjectColors({
          ...currentProjectColors,
          subcategories: {
            ...currentProjectColors.subcategories,
            [subcategory]: {
              color,
              gradientColorIndex: colorIndex,
            },
          },
        })
      );
    },
    [dispatch, selectedCategoryGradientColors, currentProjectColors]
  );

  useEffect(() => {
    const anchorRect = anchorEl.getBoundingClientRect();

    setInitialPosition({
      top: anchorRect.bottom - 10,
      left: anchorRect.left - 100,
    });
  }, [anchorEl]);

  useEffect(() => {
    setBounds({
      left: -dialogClientRect?.left,
      right: window.document.body.clientWidth - dialogClientRect?.right,
      top: -(dialogClientRect?.top - APP_HEADER_HEIGHT_IN_PX),
      bottom: window.innerHeight - dialogClientRect?.bottom,
    });
  }, [dialogClientRect]);

  const onSelectSpaceTypeColor = useCallback(
    (spaceTypeId: number, colorIndex: number) => {
      const color = selectedCategoryGradientColors[colorIndex];

      dispatch(
        setCurrentSpaceTypeColors({
          ...currentSpaceTypeColors,
          [spaceTypeId]: {
            id: spaceTypeId,
            color,
            gradientColorIndex: colorIndex,
          },
        })
      );
    },
    [dispatch, selectedCategoryGradientColors, currentSpaceTypeColors]
  );

  const handleOnClose = useCallback(() => {
    const subcategories = selectedCategory?.subcategories || [];

    const newSubcategoryColors = !isEqual(
      initialColors.subcategoryColors,
      subcategoryColors
    )
      ? subcategories
          .filter((subcategory: IProjectSpaceTypeSubcategory) => {
            const defaultSubcategory =
              subcategory.defaultSpaceTypeCategory.category;
            return (
              initialColors.subcategoryColors[defaultSubcategory]
                .gradientColorIndex !==
              subcategoryColors[defaultSubcategory].gradientColorIndex
            );
          })
          .map((subcategory: IProjectSpaceTypeSubcategory) => {
            const defaultSubcategory =
              subcategory.defaultSpaceTypeCategory.category;
            return {
              id: subcategory.id,
              ...subcategoryColors[defaultSubcategory],
            };
          })
      : [];

    const spaceTypeColors = Object.values(currentSpaceTypeColors).filter(
      (spaceTypeColor) => {
        return (
          spaceTypesById[spaceTypeColor.id].gradientColorIndex !==
          spaceTypeColor.gradientColorIndex
        );
      }
    );

    if (newSubcategoryColors.length > 0 || spaceTypeColors.length > 0) {
      dispatch(
        updateProjectColors({
          subcategoryColors: newSubcategoryColors,
          spaceTypeColors,
        })
      );
    }
    onClose();
  }, [
    initialColors,
    selectedCategory?.subcategories,
    subcategoryColors,
    currentSpaceTypeColors,
    spaceTypesById,
    dispatch,
    onClose,
  ]);

  const restoreSubcategoryColors = useCallback(() => {
    dispatch(
      setCurrentProjectColors({
        subcategories: defaultSubcategoryColors,
      })
    );
  }, [dispatch, defaultSubcategoryColors]);

  const restoreProjectColors = useCallback(() => {
    let categorySpaceTypeColors;
    if (
      selectedCategory?.defaultSpaceTypeCategory?.category ===
      INDIVIDUAL_WORKSPACES_CATEGORY
    ) {
      const categorySpaceTypes: IProjectSpaceType[] = selectedCategory.subcategories.reduce(
        (
          allSpaceTypes: IProjectSpaceType[],
          subcategory: IProjectSpaceTypeSubcategory
        ) => {
          const subcategorySpaceTypes =
            spaceTypesGroupedByCategory[subcategory?.id];

          return allSpaceTypes.concat(subcategorySpaceTypes || []);
        },
        []
      );

      categorySpaceTypeColors = buildDefaultSpaceTypeColors(
        categorySpaceTypes,
        selectedCategoryGradientColors
      );
      restoreSubcategoryColors();
    } else {
      const categorySpaceTypes =
        spaceTypesGroupedByCategory[selectedCategory?.id];

      categorySpaceTypeColors = buildDefaultSpaceTypeColors(
        categorySpaceTypes,
        selectedCategoryGradientColors
      );
    }

    dispatch(
      setCurrentSpaceTypeColors({
        ...currentSpaceTypeColors,
        ...categorySpaceTypeColors,
      })
    );
  }, [
    dispatch,
    selectedCategory,
    spaceTypesGroupedByCategory,
    restoreSubcategoryColors,
    currentSpaceTypeColors,
    selectedCategoryGradientColors,
  ]);

  const restoreSubcategorySpaceTypeColors = useCallback(
    (subcategoryId: number) => {
      const categorySpaceTypes = spaceTypesGroupedByCategory[subcategoryId];

      const subcategorySpaceTypeColors = buildDefaultSpaceTypeColors(
        categorySpaceTypes,
        selectedCategoryGradientColors
      );

      dispatch(
        setCurrentSpaceTypeColors({
          ...currentSpaceTypeColors,
          ...subcategorySpaceTypeColors,
        })
      );
    },
    [
      dispatch,
      spaceTypesGroupedByCategory,
      currentSpaceTypeColors,
      selectedCategoryGradientColors,
    ]
  );

  const isRestoreSubcategoriesEnabled = useMemo(() => {
    return !isEqual(defaultSubcategoryColors, subcategoryColors);
  }, [defaultSubcategoryColors, subcategoryColors]);

  const isRestoreSpaceTypesEnabled = useCallback(
    (categoryId: number) => {
      const categorySpaceTypes = spaceTypesGroupedByCategory[categoryId] || [];
      const initialSpaceTypeColors = buildDefaultSpaceTypeColors(
        categorySpaceTypes,
        selectedCategoryGradientColors
      );

      const selectedSpaceTypeColors = categorySpaceTypes.reduce(
        (map, projectSpaceType) => {
          return {
            ...map,
            [projectSpaceType.id]: currentSpaceTypeColors[projectSpaceType.id],
          };
        },
        {}
      );

      return !isEqual(initialSpaceTypeColors, selectedSpaceTypeColors);
    },
    [
      spaceTypesGroupedByCategory,
      currentSpaceTypeColors,
      selectedCategoryGradientColors,
    ]
  );

  const isRestoreAllEnabled = useMemo(() => {
    if (
      selectedCategory?.defaultSpaceTypeCategory?.category ===
      INDIVIDUAL_WORKSPACES_CATEGORY
    ) {
      const { subcategories } = selectedCategory;
      return (
        isRestoreSubcategoriesEnabled ||
        subcategories.some((subcategory: IProjectSpaceTypeSubcategory) =>
          isRestoreSpaceTypesEnabled(subcategory.id)
        )
      );
    }
    return isRestoreSpaceTypesEnabled(selectedCategory?.id);
  }, [
    selectedCategory,
    isRestoreSubcategoriesEnabled,
    isRestoreSpaceTypesEnabled,
  ]);

  const height = useMemo(() => {
    if (!anchorEl) return 0;
    const clientRect = anchorEl.getBoundingClientRect();
    const minHeight = window.innerHeight - clientRect.bottom;
    return Math.min(minHeight, DIALOG_HEIGHT);
  }, [anchorEl]);

  const categorySpaceTypesBreakdown: {
    [index: number]: ISpaceBreakDown;
  } = spacesBreakdown[
    selectedCategory?.defaultSpaceTypeCategory?.category
  ]?.reduce((map, spaceTypeBreakdown: ISpaceBreakDown) => {
    return {
      ...map,
      [spaceTypeBreakdown.spaceTypeId]: spaceTypeBreakdown,
    };
  }, {});

  const doesCategoryHaveSpaces =
    Object.keys(categorySpaceTypesBreakdown ?? {})?.length > 0;

  const onWindowResize = useCallback(() => {
    const anchorRect = anchorEl.getBoundingClientRect();
    const left = anchorRect.left - 100;
    const top = anchorRect?.bottom - 10;

    setInitialPosition({ top, left });

    setBounds({
      left: -left,
      right: window.document.body.clientWidth - (left + DIALOG_WIDTH),
      top: -(top - APP_HEADER_HEIGHT_IN_PX),
      bottom: window.innerHeight - (top + dialogClientRect.height),
    });
  }, [anchorEl, dialogClientRect]);

  useEffect(() => {
    window.addEventListener('resize', onWindowResize);
    return () => {
      window.removeEventListener('resize', onWindowResize);
    };
  }, [onWindowResize]);

  const subcategories = individualWorkspacesCategory.subcategories.filter(
    (subcategory) => {
      const categorySpaceTypes =
        spacesBreakdown[
          individualWorkspacesCategory.defaultSpaceTypeCategory.category
        ];
      return categorySpaceTypes?.some((spaceTypeBreakdown) => {
        return (
          spaceTypeBreakdown.subcategory ===
          subcategory.defaultSpaceTypeCategory.category
        );
      });
    }
  );

  return (
    <DraggableDialog
      anchorEl={anchorEl}
      open={!isNil(anchorEl)}
      defaultPosition={initialPosition}
      width={DIALOG_WIDTH}
      bounds={bounds}
      processRefNode={processRefNode}
      height={height}
      gripperWrapperStyles={{ cursor: 'grab' }}
      title={
        <PopoverHeader>
          <Row justifyContent="space-beetween">
            <Box flexGrow={1} className="title">
              Edit Colors
            </Box>
            <IconButton
              onClick={handleOnClose}
              size="small"
              style={{ padding: 0 }}
            >
              <Clear data-cy="close-popover-icon" />
            </IconButton>
          </Row>
        </PopoverHeader>
      }
    >
      <PopoverContent>
        <Box>
          <Section>
            <Row mt={1}>
              <Box>
                <Typography variant="h5">
                  {selectedCategory?.name?.toUpperCase()}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="flex-end" flex={1}>
                <RestoreButton
                  onClick={isRestoreAllEnabled ? restoreProjectColors : null}
                  disabled={!isRestoreAllEnabled}
                >
                  <RestoreImage
                    src={isRestoreAllEnabled ? RestoreIcon : RestoreGrayIcon}
                    alt="Restore"
                  />
                  Restore All
                </RestoreButton>
              </Box>
            </Row>
            <Box display="flex" justifyContent="space-between" mt={2}>
              {categories.map((spaceTypeCategory: ProjectCategory) => (
                <CategoryColorBubble
                  key={spaceTypeCategory.id}
                  className={`${
                    selectedCategory?.defaultSpaceTypeCategory?.category ===
                    spaceTypeCategory?.defaultSpaceTypeCategory.category
                      ? 'selected'
                      : ''
                  }`}
                >
                  <SmallCategoryColorBubble
                    color={spaceTypeCategory.color}
                    onClick={() => {
                      setSelectedCategory(spaceTypeCategory);
                    }}
                  />
                </CategoryColorBubble>
              ))}
            </Box>
            <Box>
              <p>
                Select gradients for {selectedCategory?.name}. Go to Project
                Profile to change category color.
              </p>
            </Box>
          </Section>
          {selectedCategory?.defaultSpaceTypeCategory?.category ===
          INDIVIDUAL_WORKSPACES_CATEGORY ? (
            <>
              {subcategories.length > 0 ? (
                <Box>
                  <Section
                    className={doesCategoryHaveSpaces ? '' : 'borderless'}
                  >
                    <Box>
                      <Typography variant="h5" mt={2} mb={1}>
                        SPACE TYPE ALLOCATION
                      </Typography>
                      <Box
                        display="flex"
                        alignItems="center"
                        className="subtitle"
                        mb={1}
                      >
                        <Box flexGrow={1}>Subcategories</Box>
                        <Box display="flex" justifyContent="flex-end">
                          <RestoreButton
                            onClick={
                              isRestoreSubcategoriesEnabled
                                ? restoreSubcategoryColors
                                : null
                            }
                            disabled={!isRestoreSubcategoriesEnabled}
                          >
                            <RestoreImage
                              src={
                                isRestoreSubcategoriesEnabled
                                  ? RestoreIcon
                                  : RestoreGrayIcon
                              }
                              alt="Restore"
                            />
                            Restore
                          </RestoreButton>
                        </Box>
                      </Box>
                    </Box>
                    {subcategories.map((subcategory) => {
                      const subcategoryColor =
                        subcategoryColors[
                          subcategory.defaultSpaceTypeCategory.category
                        ];

                      const subcategoryColorIndex =
                        subcategoryColor.gradientColorIndex;

                      return (
                        <div key={subcategory.id}>
                          <SubcategoryColorGradient
                            title={
                              <GradientTitle>
                                All {subcategory.name}
                              </GradientTitle>
                            }
                            gradientColorIndex={subcategoryColorIndex}
                            gradientColors={selectedCategoryGradientColors}
                            subcategory={
                              subcategory.defaultSpaceTypeCategory.category
                            }
                            onSelectSubcategoryColor={onSelectSubcategoryColor}
                          />
                        </div>
                      );
                    })}
                  </Section>

                  <Box>
                    <Typography
                      variant="h5"
                      mt={2}
                      mb={1}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      {individualWorkspacesCategory.name.toUpperCase()} USF AND
                      SEAT MIX
                    </Typography>
                    {subcategories.map((subcategory) => {
                      const subcategorySpaceTypes = spaceTypesGroupedByCategory[
                        subcategory.id
                      ]?.filter((projectSpaceType) => {
                        return !isNil(
                          categorySpaceTypesBreakdown[
                            projectSpaceType.spaceType.id
                          ]
                        );
                      });

                      const isRestoreEnabled = isRestoreSpaceTypesEnabled(
                        subcategory.id
                      );

                      return subcategorySpaceTypes?.length > 0 ? (
                        <Box key={subcategory.id}>
                          <Box
                            display="flex"
                            alignItems="center"
                            className="subtitle"
                            mb={1}
                            key={subcategory.id}
                          >
                            <Box flexGrow={1}>{subcategory.name}</Box>
                            <Box display="flex" justifyContent="flex-end">
                              <RestoreButton
                                onClick={() =>
                                  isRestoreEnabled
                                    ? restoreSubcategorySpaceTypeColors(
                                        subcategory.id
                                      )
                                    : null
                                }
                                disabled={!isRestoreEnabled}
                              >
                                <RestoreImage
                                  src={
                                    isRestoreEnabled
                                      ? RestoreIcon
                                      : RestoreGrayIcon
                                  }
                                  alt="Restore"
                                />
                                Restore
                              </RestoreButton>
                            </Box>
                          </Box>
                          {subcategorySpaceTypes?.map((projectSpaceType) => {
                            const spaceTypeColor =
                              currentSpaceTypeColors[projectSpaceType.id];

                            return (
                              <div key={projectSpaceType.id}>
                                <SpaceTypeColorGradient
                                  title={
                                    <GradientTitle>
                                      {clientFacingNamesAreVisible &&
                                      projectSpaceType.clientFacingName
                                        ? projectSpaceType.clientFacingName
                                        : projectSpaceType.spaceType.name}
                                    </GradientTitle>
                                  }
                                  gradientColors={
                                    selectedCategoryGradientColors
                                  }
                                  gradientColorIndex={
                                    spaceTypeColor.gradientColorIndex
                                  }
                                  projectSpaceTypeId={projectSpaceType.id}
                                  onSelectSpaceTypeColor={
                                    onSelectSpaceTypeColor
                                  }
                                />
                              </div>
                            );
                          })}
                        </Box>
                      ) : null;
                    })}
                  </Box>
                </Box>
              ) : (
                <div>
                  <p>There are no spaces added yet to edit.</p>
                </div>
              )}
            </>
          ) : (
            <Box>
              {doesCategoryHaveSpaces ? (
                <>
                  <Box>
                    <Typography
                      variant="h5"
                      mt={2}
                      mb={1}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      {selectedCategory?.name?.toUpperCase()} USF AND RATIOS
                    </Typography>
                  </Box>
                  {spaceTypesGroupedByCategory[selectedCategory?.id]
                    ?.filter((projectSpaceType: IProjectSpaceType) => {
                      return !isNil(
                        categorySpaceTypesBreakdown[
                          projectSpaceType.spaceType.id
                        ]
                      );
                    })
                    .map((projectSpaceType: IProjectSpaceType) => {
                      const spaceTypeColor =
                        currentSpaceTypeColors[projectSpaceType.id];
                      return (
                        <div key={projectSpaceType.id}>
                          <SpaceTypeColorGradient
                            title={
                              <GradientTitle>
                                {clientFacingNamesAreVisible &&
                                projectSpaceType.clientFacingName
                                  ? projectSpaceType.clientFacingName
                                  : projectSpaceType.spaceType.name}
                              </GradientTitle>
                            }
                            gradientColors={selectedCategoryGradientColors}
                            gradientColorIndex={
                              spaceTypeColor.gradientColorIndex
                            }
                            projectSpaceTypeId={projectSpaceType.id}
                            onSelectSpaceTypeColor={onSelectSpaceTypeColor}
                          />
                        </div>
                      );
                    })}
                </>
              ) : (
                <div>
                  <p>There are no spaces added yet to edit.</p>
                </div>
              )}
            </Box>
          )}
        </Box>
      </PopoverContent>
    </DraggableDialog>
  );
};

export default SpaceTypeColorsPopover;
